/* ──────────────────────────────────────────────────────────────────────────
 * AuditLogPanel — the per-user IPO journey audit trail, surfaced inside the
 * IPO Navigator "Audit Log" left-panel view. Shows a complete, chronological
 * record of what was done, when, and by whom (start → end of the IPO journey),
 * generated fresh for every user. Reads GET /api/workspace/audit-log.
 * ────────────────────────────────────────────────────────────────────────── */
import { useCallback, useEffect, useState } from "react";
import "./AuditLogPanel.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_META = {
  "Dashboard": { ic: "ti-layout-dashboard", c: "#185FA5" },
  "IPO Navigator": { ic: "ti-compass", c: "#185FA5" },
  "Journey Planner": { ic: "ti-road", c: "#1D9E75" },
  "Readiness Score": { ic: "ti-file-check", c: "#534AB7" },
  "Raise Funds": { ic: "ti-cash", c: "#BA7517" },
  "Advisor Marketplace": { ic: "ti-users-group", c: "#0C447C" },
  "Document Vault": { ic: "ti-folder-lock", c: "#1D9E75" },
  "DRHP Workspace": { ic: "ti-file-text", c: "#6C4BF4" },
  "Valuation Studio": { ic: "ti-calculator", c: "#A32D2D" },
  "Audit Log": { ic: "ti-history", c: "#5f5e5a" },
  "Oracle": { ic: "ti-robot", c: "#185FA5" },
};
const metaFor = (m) => MODULE_META[m] || { ic: "ti-point", c: "#8a8a94" };

const dayKey = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch { return "Earlier"; }
};
const timeStr = (iso) => {
  try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export const AuditLogPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/api/workspace/audit-log`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const events = data?.events || [];

  // Group consecutive events by calendar day (events come newest-first).
  const groups = [];
  events.forEach((ev) => {
    const key = dayKey(ev.created_at);
    if (!groups.length || groups[groups.length - 1].key !== key) {
      groups.push({ key, items: [] });
    }
    groups[groups.length - 1].items.push(ev);
  });

  return (
    <div className="audit-log-root" data-testid="audit-log-panel">
      <div className="al-head">
        <div>
          <div className="al-title">Audit Log</div>
          <div className="al-sub" data-testid="al-context">
            Complete journey trail for{" "}
            <strong>{data?.companyName || "your company"}</strong>
            {data?.userName ? <> · {data.userName}</> : null}
            {typeof data?.count === "number" ? <> · {data.count} event{data.count === 1 ? "" : "s"}</> : null}
          </div>
        </div>
        <button className="al-refresh" onClick={load} disabled={loading} data-testid="al-refresh-btn">
          <i className={`ti ti-refresh ${loading ? "spin" : ""}`} aria-hidden="true" /> Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="al-empty" data-testid="al-loading">Loading your journey…</div>
      ) : events.length === 0 ? (
        <div className="al-empty" data-testid="al-empty">
          <i className="ti ti-history" aria-hidden="true" />
          <div>No journey steps recorded yet.</div>
          <div className="al-empty-sub">As you set up your workspace, engage advisors, upload documents and draft your DRHP, every step is recorded here.</div>
        </div>
      ) : (
        <div className="al-timeline" data-testid="al-timeline">
          {groups.map((g) => (
            <div className="al-group" key={g.key}>
              <div className="al-daylabel">{g.key}</div>
              {g.items.map((ev, i) => {
                const meta = metaFor(ev.module);
                return (
                  <div className="al-row" key={ev.event_id || i} data-testid="al-row">
                    <div className="al-dotcol">
                      <span className="al-dot" style={{ background: meta.c }}>
                        <i className={`ti ${meta.ic}`} aria-hidden="true" />
                      </span>
                      <span className="al-line" />
                    </div>
                    <div className="al-body">
                      <div className="al-row-top">
                        <span className="al-action">{ev.action}</span>
                        <span className="al-module" style={{ color: meta.c, borderColor: meta.c }}>{ev.module}</span>
                        <span className="al-time">{timeStr(ev.created_at)}</span>
                      </div>
                      <div className="al-detail">{ev.detail}</div>
                      {ev.user_name ? <div className="al-by">by {ev.user_name}</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditLogPanel;
